/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { FilterSchema } from '@modules/cui/FilterContext'
import Feature from '@modules/utils/feature'

import { getConfigForKey, isFeatureActivated } from '../../../store'

export function getSchema() {
  const isAnyAdminconsole = getConfigForKey(`APP_NAME`) === `adminconsole`
  const isSaasAdminconsole = getConfigForKey(`CLOUD_UI_APP`) === `saas-adminconsole`
  const hasSaasFilters = isFeatureActivated(Feature.saasFilters)
  const canToggleDeploymentLock = isFeatureActivated(Feature.toggleDeploymentLock)
  const canSearchClusterLock = isFeatureActivated(Feature.searchClusterLock)
  const hideInsteadOfDelete = isFeatureActivated(Feature.hideClusterInsteadOfDelete)

  const clusterLockingFields: FilterSchema['fields'] =
    canToggleDeploymentLock && canSearchClusterLock
      ? {
          locked: {
            type: `string`,
            validate: validateHumanBool,
          },
        }
      : {}

  const anyAdminconsoleFields: FilterSchema['fields'] = isAnyAdminconsole
    ? {
        system: {
          type: `string`,
          validate: validateHumanBool,
        },
        system_initiated: {
          type: `string`,
          validate: validateHumanBool,
        },
        allocator: {
          type: `string`,
        },
      }
    : {}

  const saasAdminconsoleFields: FilterSchema['fields'] = isSaasAdminconsole
    ? {
        organization: {
          type: `string`,
        },
        subscription: {
          type: `string`,
        },
      }
    : {}

  const hiddenClusterFields: FilterSchema['fields'] = hideInsteadOfDelete
    ? {
        hidden: {
          type: `string`,
          validate: validateHumanBool,
        },
      }
    : {}

  const saasFields: FilterSchema['fields'] = hasSaasFilters
    ? {
        region: {
          type: `string`,
        },
      }
    : {}

  const schema: FilterSchema = {
    strict: true,
    fields: {
      id: {
        type: `string`,
      },
      name: {
        type: `string`,
      },
      healthy: {
        type: `string`,
        validate: validateHumanBool,
      },
      healthy_configuration: {
        type: `string`,
        validate: validateHumanBool,
      },
      healthy_masters: {
        type: `string`,
        validate: validateHumanBool,
      },
      healthy_snapshot: {
        type: `string`,
        validate: validateHumanBool,
      },
      healthy_snapshot_latest: {
        type: `string`,
        validate: validateHumanBool,
      },
      enabled_snapshots: {
        type: `string`,
        validate: validateHumanBool,
      },
      maintenance: {
        type: `string`,
        validate: validateHumanBool,
      },
      stopped: {
        type: `string`,
        validate: validateHumanBool,
      },
      pending: {
        type: `string`,
        validate: validateHumanBool,
      },
      version: {
        type: `string`,
      },
      has: {
        type: `string`,
        validate: validateProduct,
      },
      configuration: {
        type: `string`,
      },
      template: {
        type: `string`,
      },
      zones: {
        type: `number`,
      },
      size: {
        type: `number`,
      },
      source: {
        type: `string`,
      },
      since: {
        type: `string`,
      },
      until: {
        type: `string`,
      },
      ...clusterLockingFields,
      ...saasAdminconsoleFields,
      ...saasFields,
      ...anyAdminconsoleFields,
      ...hiddenClusterFields,
    },
  }

  const defaultFields = [`id`, `name`, `version`, `source`]

  if (`region` in schema) {
    defaultFields.push(`region`)
  }

  return { schema, defaultFields }
}

function validateHumanBool(value) {
  if (value !== `y` && value !== `n`) {
    throw new Error(`Expected "y" or "n"`)
  }
}

function getActiveProducts() {
  return [`kibana`, `ml`, `apm`]
}

function validateProduct(value) {
  const products = getActiveProducts()

  if (!products.includes(value)) {
    throw new Error(`Expected one of: "${products.join(`", "`)}"`)
  }
}
